import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import AdminLayout from '../../layouts/AdminLayout';
import StatsCard from '../../components/admin/StatsCard';
import DashboardHero from '../../components/admin/DashboardHero';
import Spinner from '../../components/common/Spinner';
import { getDashboardStats } from '../../api/admin.api';

const QuickAction = ({ to, icon, label, desc, primary }) => (
  <Link
    to={to}
    className={`group flex items-center gap-4 p-4 rounded-2xl border transition-all duration-150 hover:shadow-card-md ${
      primary
        ? 'bg-primary-gradient text-white border-primary-600 hover:opacity-90'
        : 'bg-white text-slate-700 border-slate-200 hover:border-primary-200 hover:bg-primary-50'
    }`}
  >
    <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-xl flex-shrink-0 ${
      primary ? 'bg-white/20' : 'bg-slate-100 group-hover:bg-primary-100'
    }`}>
      {icon}
    </div>
    <div>
      <p className={`text-sm font-semibold ${primary ? 'text-white' : 'text-slate-800'}`}>{label}</p>
      <p className={`text-xs mt-0.5 ${primary ? 'text-white/70' : 'text-slate-400'}`}>{desc}</p>
    </div>
    <svg className={`w-4 h-4 ml-auto flex-shrink-0 opacity-50 group-hover:opacity-100 transition-opacity ${primary ? 'text-white' : 'text-slate-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  </Link>
);

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const user = useSelector((s) => s.auth.user);

  useEffect(() => {
    getDashboardStats()
      .then((res) => setStats(res.data.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  return (
    <AdminLayout>
      {/* Cinematic hero */}
      <DashboardHero
        greeting={greeting}
        name={user?.name}
        subtitle="Here's what's happening on your assessment platform."
        stats={stats}
      />

      {/* Stats grid */}
      {loading ? (
        <Spinner className="py-20" />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 mb-8">
          <StatsCard label="Total Exams" value={stats?.totalExams} icon="📝" color="blue" />
          <StatsCard label="Candidates Registered" value={stats?.totalCandidates} icon="👥" color="purple" />
          <StatsCard label="Total Attempts" value={stats?.totalAttempts} icon="✅" color="green" />
          <StatsCard label="Average Score" value={stats?.avgPercentage ? `${stats.avgPercentage}%` : '—'} icon="📊" color="yellow" />
          <StatsCard label="Total Passed" value={stats?.totalPassed} icon="🏆" color="green" />
          <StatsCard label="Active Sessions" value={stats?.activeSessions} icon="🔴" color="yellow" />
        </div>
      )}

      {/* Quick actions */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-card">
        <h2 className="text-base font-semibold text-slate-800 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <QuickAction
            to="/admin/exams/new"
            icon="✏️"
            label="Create Exam"
            desc="Build a new assessment"
            primary
          />
          <QuickAction
            to="/admin/exams"
            icon="📋"
            label="Manage Exams"
            desc="View and edit all exams"
          />
          <QuickAction
            to="/admin/reports"
            icon="📈"
            label="View Reports"
            desc="Results and analytics"
          />
        </div>
      </div>
    </AdminLayout>
  );
};

export default Dashboard;
